import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';
import { api } from '../lib/api';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadProfile = async () => {
    try {
      // Add timeout to prevent hanging (reduced to 5 seconds)
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request timeout')), 5000)
      );

      const profileData = await Promise.race([
        api.get('/api/auth/me'),
        timeoutPromise
      ]);

      setProfile(profileData);
    } catch (error) {
      // Only clear profile on explicit auth failure (401). Timeout/network errors when returning
      // from background tab are transient - keep existing profile to avoid false logout.
      if (error?.response?.status === 401) {
        setProfile(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const initialSessionHandledRef = useRef(false);

  useEffect(() => {
    let mounted = true;

    const finishInitialAuth = () => {
      if (!mounted || initialSessionHandledRef.current) return;
      initialSessionHandledRef.current = true;
      setLoading(false);
    };

    const handleSession = async (session) => {
      if (!mounted) return;
      if (session) {
        setUser(session.user);
        await loadProfile();
      } else {
        setUser(null);
        setProfile(null);
      }
      finishInitialAuth();
    };

    // Gunakan onAuthStateChange sebagai sumber kebenaran utama.
    // INITIAL_SESSION adalah satu-satunya event yang diproses saat initial load.
    // SIGNED_IN bisa terpanggil lebih dulu (saat refresh) dan memicu loadProfile yang timeout ->
    // profile=null, loading=false -> blink ke login. Jadi kita abaikan event lain sampai
    // INITIAL_SESSION diproses.
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!mounted) return;

      if (event === 'INITIAL_SESSION') {
        await handleSession(session);
      } else if (initialSessionHandledRef.current && session) {
        setUser(session.user);
        await loadProfile();
      } else if (initialSessionHandledRef.current && !session) {
        setUser(null);
        setProfile(null);
        finishInitialAuth();
      }
    });

    // Fallback: jika INITIAL_SESSION tidak terpanggil dalam 5 detik (edge case)
    const fallbackTimeout = setTimeout(() => {
      if (mounted && !initialSessionHandledRef.current) {
        supabase.auth.getSession().then(({ data: { session } }) => {
          if (mounted && !initialSessionHandledRef.current) {
            handleSession(session);
          }
        });
      }
    }, 5000);

    return () => {
      mounted = false;
      subscription.unsubscribe();
      clearTimeout(fallbackTimeout);
    };
  }, []);

  const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
    return data;
  };

  const signUp = async (email, password, fullName, role = 'hunter') => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: fullName,
          role: role,
        },
      },
    });
    if (error) throw error;
    return data;
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setProfile(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        profile,
        loading,
        signIn,
        signUp,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

